import axios, { AxiosResponse } from 'axios';
import { ApiError, ApiErrorType } from './error';
import { Operation, OperationType } from '../model/operation';
import * as skillPublishingKeyset from 'i18n/SkillPublishing';
import i18n from '@yandex-int/i18n';

const tSkillPublishing = i18n(skillPublishingKeyset);

interface CallParams {
    method: string;
    url: string;
    data?: FormData | object;
    onUploadProgress?: (progressEvent: unknown) => void;
    queryParams?: Record<string, unknown>;
    timeout?: number;
}

function callWithoutWrap<T>({ data, method, url, onUploadProgress, timeout, queryParams }: CallParams) {
    const headers = {
        'x-csrf-token': __appConfig.secretkey,
        ...(!(data instanceof FormData) && {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        }),
    };
    const body = !data || data instanceof FormData ? data : JSON.stringify(data);

    return axios<CallParams, AxiosResponse<T>>(url, {
        data: body,
        withCredentials: true,
        headers,
        method,
        onUploadProgress,
        params: queryParams,
        validateStatus: () => true,
        timeout,
    });
}

export function callPublicApi<T = void>(params: CallParams): Promise<T> {
    return callWithoutWrap<T>(params).then(async (res) => {
        const body = res.data;

        if (res.status >= 200 && res.status < 300) {
            return body;
        }

        throw body;
    });
}

export function callApi<T = void>(params: CallParams): Promise<T> {
    return callWithoutWrap<T>(params)
        .then((response) => {
            if (response.status === 429) {
                throw new ApiError(response.statusText, { code: response.status });
            }

            return response;
        })
        .then((response) => {
            const data = response.data;

            if (isError(data)) {
                const { message, code, fields } = data.error;
                const { payload } = data;

                throw new ApiError(message, { code, fields, payload });
            }

            if (
                response.status >= 400 &&
                data &&
                typeof data === 'object' &&
                'message' in data &&
                typeof data.message === 'string'
            ) {
                throw new ApiError(data.message, { code: response.status });
            }

            if (response.status >= 400 && typeof data === 'string') {
                throw new ApiError(data, { code: response.status });
            }

            if (response.status >= 400) {
                throw new ApiError('Unknown error', { code: response.status });
            }

            if (isResult(data)) {
                return data.result as T;
            }

            if (response.status === 200) {
                return data as T;
            }

            throw new Error('Malformed API response');
        });
}

interface ResultWrapper<Result> {
    result: Result;
}

interface ErrorWrapper {
    error: ApiError;
    payload: ApiErrorType['payload'];
}

const isError = (res: unknown): res is ErrorWrapper => {
    return Boolean(res && typeof res === 'object' && 'error' in res && res.error);
};

const isResult = <Result>(res: unknown): res is ResultWrapper<Result> => {
    return Boolean(res && typeof res === 'object' && 'result' in res && res.result);
};

export function sleep(delay: number) {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    });
}

export const translateOperations = (operations: Array<Operation>) => {
    return operations.map((operation) => {
        let comment = operation.comment;
        if (operation.comment === skillPublishingKeyset.ru['unknown-error']) {
            comment = tSkillPublishing('unknown-error');
        }
        if (
            operation.comment === skillPublishingKeyset.en['publication-canceled-by-user'] &&
            operation.type === OperationType.ReviewCancelled
        ) {
            comment = tSkillPublishing('publication-canceled-by-user');
        }
        return {
            ...operation,
            comment,
        };
    });
};
